<script lang="ts">
  /**
   * Réglages — reprend ce que faisait le menu `⋯` de la topbar (compte,
   * état du Worker, déconnexions) et y ajoute le choix de thème.
   *
   * Les groupes « Gestes de balayage » et « Application » du handoff ne sont
   * pas encore là : ils demandent une configuration persistée que
   * `swipeRow.ts` doit relire. Le cadre est posé pour les recevoir.
   */

  import { authState } from "../auth/store.svelte";
  import { crmSession, crmSignOut } from "../crm/session.svelte";
  import { themeState, setTheme, type ThemeChoice } from "../theme.svelte";
  import { pushState } from "../push/store.svelte";
  import { clearAllCaches } from "../gmail/inboxCache";
  import { haptic } from "../gestures/haptic";
  import Icon, { type IconName } from "./shared/Icon.svelte";
  import {
    settingsState,
    setSwipe,
    setHaptic,
    setTrackByDefault,
    setOfflineCache,
    GESTURE_ORDER,
    GESTURE_LABELS,
    SWIPE_ACTION_LABELS,
    type SwipeAction,
    type Gesture,
  } from "../settings.svelte";

  interface Props {
    workerUrl: string;
    workerReachable: boolean | null;
    onSignOut: () => void;
  }

  let { workerUrl, workerReachable, onSignOut }: Props = $props();

  const THEMES: Array<{ id: ThemeChoice; label: string }> = [
    { id: "system", label: "Système" },
    { id: "light", label: "Clair" },
    { id: "dark", label: "Sombre" },
  ];

  const initials = $derived.by(() => {
    const src = authState.email ?? "?";
    const name = src.split("@")[0];
    const parts = name.split(/[.\-_]+/).filter(Boolean);
    if (parts.length === 0) return "?";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
  });

  function pickTheme(id: ThemeChoice) {
    haptic("light");
    setTheme(id);
  }

  const ACTIONS = Object.keys(SWIPE_ACTION_LABELS) as SwipeAction[];
  const ACTION_ICON: Record<SwipeAction, IconName> = {
    archive: "archive",
    later: "star",
    snooze: "clock",
    read: "check",
    reply: "reply",
    label: "filter",
  };
  /** Teinte de la vignette : celle que le geste révèle sous le doigt. */
  const ACTION_TONE: Record<SwipeAction, string> = {
    archive: "accent",
    later: "neutral",
    snooze: "warn",
    read: "neutral",
    reply: "accent",
    label: "neutral",
  };

  /** Éteindre le cache purge ce qui restait : sinon on garde sur l'appareil
   *  des mails que l'utilisateur vient de demander de ne plus garder. */
  async function onToggleCache() {
    const next = !settingsState.offlineCache;
    setOfflineCache(next);
    if (!next) await clearAllCaches();
  }

  function onSwipeChange(g: Gesture, e: Event) {
    setSwipe(g, (e.currentTarget as HTMLSelectElement).value as SwipeAction);
    haptic("light");
  }
</script>

<div class="settings">
  <header>
    <h1>Réglages</h1>
    <div class="account">
      <div class="avatar" aria-hidden="true">{initials}</div>
      <div class="who">
        <div class="email">{authState.email ?? "—"}</div>
        <div class="status" class:off={workerReachable === false}>
          <span class="dot" aria-hidden="true"></span>
          {#if workerReachable === false}
            Worker injoignable
          {:else if workerReachable === null}
            Connexion…
          {:else}
            Connecté
          {/if}
        </div>
      </div>
    </div>
  </header>

  <div class="body">
    <section class="group">
      <h2>Apparence</h2>
      <div class="row">
        <span class="lab">Thème</span>
        <div class="segmented" role="group" aria-label="Thème">
          {#each THEMES as t (t.id)}
            <button
              class="seg"
              class:on={themeState.choice === t.id}
              aria-pressed={themeState.choice === t.id}
              onclick={() => pickTheme(t.id)}
            >{t.label}</button>
          {/each}
        </div>
      </div>
    </section>

    <section class="group">
      <h2>Comptes</h2>
      <div class="row col">
        <span class="lab">Google</span>
        <span class="val">{authState.email ?? "—"}</span>
      </div>
      <div class="row col">
        <span class="lab">Miroir CRM</span>
        <span class="val">
          {crmSession.status === "in" ? crmSession.email : "Non connecté"}
        </span>
      </div>
      <div class="row col">
        <span class="lab">Worker</span>
        <span class="val mono">{workerUrl}</span>
      </div>
      <div class="row col">
        <span class="lab">Notifications</span>
        <span class="val">
          {pushState.subscribed ? "Actives sur cet appareil" : "Inactives"}
        </span>
      </div>
    </section>

    <section class="group">
      <h2>Gestes de balayage</h2>
      {#each GESTURE_ORDER as g (g)}
        {@const action = settingsState.swipe[g]}
        <div class="row">
          <span class="thumb tone-{ACTION_TONE[action]}" aria-hidden="true">
            <Icon name={ACTION_ICON[action]} size={14} />
          </span>
          <span class="lab grow">{GESTURE_LABELS[g]}</span>
          <select
            class="picker"
            value={action}
            onchange={(e) => onSwipeChange(g, e)}
            aria-label={GESTURE_LABELS[g]}
          >
            {#each ACTIONS as a (a)}
              <option value={a}>{SWIPE_ACTION_LABELS[a]}</option>
            {/each}
          </select>
        </div>
      {/each}
    </section>

    <section class="group">
      <h2>Application</h2>
      <div class="row">
        <span class="col-lab">
          <span class="lab strong">Retour haptique</span>
          <span class="desc">Vibration sur les gestes de balayage</span>
        </span>
        <button
          class="switch"
          class:on={settingsState.haptic}
          role="switch"
          aria-checked={settingsState.haptic}
          aria-label="Retour haptique"
          onclick={() => setHaptic(!settingsState.haptic)}
        >
          <span class="knob"></span>
        </button>
      </div>
      <div class="row">
        <span class="col-lab">
          <span class="lab strong">Suivi d'ouverture par défaut</span>
          <span class="desc">Actif sur chaque envoi depuis la PWA</span>
        </span>
        <button
          class="switch"
          class:on={settingsState.trackByDefault}
          role="switch"
          aria-checked={settingsState.trackByDefault}
          aria-label="Suivi d'ouverture par défaut"
          onclick={() => setTrackByDefault(!settingsState.trackByDefault)}
        >
          <span class="knob"></span>
        </button>
      </div>
      <div class="row">
        <span class="col-lab">
          <span class="lab strong">Cache hors-ligne</span>
          <span class="desc">Les fils déjà chargés restent lisibles sans réseau</span>
        </span>
        <button
          class="switch"
          class:on={settingsState.offlineCache}
          role="switch"
          aria-checked={settingsState.offlineCache}
          aria-label="Cache hors-ligne"
          onclick={onToggleCache}
        >
          <span class="knob"></span>
        </button>
      </div>
      <div class="row">
        <span class="col-lab">
          <span class="lab strong">Notifications push</span>
          <span class="desc">Nouveaux mails et ouvertures suivies</span>
        </span>
        <span class="val">{pushState.subscribed ? "Actives" : "Inactives"}</span>
      </div>
    </section>

    {#if crmSession.status === "in"}
      <button class="danger ghost" onclick={() => void crmSignOut()}>
        Déconnecter le miroir CRM
      </button>
    {/if}
    <button class="danger" onclick={onSignOut}>Se déconnecter</button>

    <p class="foot">Mailfox PWA · m.autonao.com</p>
  </div>
</div>

<style>
  .settings {
    min-height: 100%;
    background: var(--canvas);
  }

  header {
    background: var(--bg);
    border-bottom: 1px solid var(--border-subtle);
    padding: calc(env(safe-area-inset-top) + 14px) 16px 16px;
  }
  h1 {
    margin: 0;
    font-family: var(--font-serif);
    font-style: italic;
    font-weight: 400;
    font-size: 31px;
    line-height: 1;
    letter-spacing: -0.015em;
  }

  .account {
    display: flex;
    align-items: center;
    gap: 11px;
    margin-top: 14px;
  }
  .avatar {
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    border-radius: 50%;
    display: grid;
    place-items: center;
    background: var(--accent-soft);
    color: var(--accent);
    font-size: 14px;
    font-weight: 600;
  }
  .who { min-width: 0; }
  .email {
    font-size: 13.5px;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .status {
    display: flex;
    align-items: center;
    gap: 5px;
    margin-top: 2px;
    font-family: var(--font-mono);
    font-size: 9.5px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--good);
  }
  .status.off { color: var(--bad); }
  .dot {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: currentColor;
  }

  .body { padding: 14px 12px 24px; }

  .group {
    background: var(--bg);
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-group);
    overflow: hidden;
    margin-bottom: 12px;
  }
  h2 {
    margin: 0;
    padding: 11px 14px 3px;
    font-family: var(--font-mono);
    font-size: 9px;
    font-weight: 500;
    letter-spacing: 0.09em;
    text-transform: uppercase;
    color: var(--fg-subtle);
  }
  .row {
    display: flex;
    align-items: center;
    gap: 10px;
    justify-content: space-between;
    padding: 11px 14px;
    border-top: 1px solid var(--border-subtle);
  }
  .row.col {
    align-items: baseline;
  }
  .lab {
    font-size: 12.5px;
    color: var(--fg-muted);
    flex-shrink: 0;
  }
  .val {
    font-size: 12.5px;
    color: var(--fg);
    text-align: right;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .val.mono {
    font-family: var(--font-mono);
    font-size: 10.5px;
    color: var(--fg-subtle);
  }
  .lab.grow { flex: 1; }
  .lab.strong { color: var(--fg); font-size: 13px; }
  .col-lab { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
  .desc {
    font-size: 11.5px;
    line-height: 1.4;
    color: var(--fg-subtle);
  }

  .thumb {
    width: 26px;
    height: 26px;
    flex-shrink: 0;
    display: grid;
    place-items: center;
    border-radius: var(--radius-md);
    background: var(--bg-hover);
    color: var(--fg-muted);
  }
  .thumb.tone-accent { background: var(--accent-soft); color: var(--accent); }
  .thumb.tone-warn { background: var(--warn-bg); color: var(--warn); }

  .picker {
    height: 27px;
    padding: 0 8px;
    border: 1px solid var(--border);
    border-radius: var(--radius-btn);
    background: var(--bg);
    color: var(--fg);
    font-size: 12px;
    font-weight: 500;
  }

  /* Interrupteur : piste + bouton, 140 ms comme le handoff. */
  .switch {
    position: relative;
    width: 42px;
    height: 25px;
    flex-shrink: 0;
    border-radius: var(--radius-pill);
    background: var(--bg-active);
    transition: background 140ms ease;
  }
  .switch.on { background: var(--accent); }
  .knob {
    position: absolute;
    top: 2.5px;
    left: 2.5px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
    transition: left 140ms ease;
  }
  .switch.on .knob { left: 19.5px; }

  .segmented {
    display: flex;
    gap: 0;
    border: 1px solid var(--border);
    border-radius: var(--radius-btn);
    overflow: hidden;
  }
  .seg {
    padding: 5px 11px;
    font-size: 12px;
    font-weight: 500;
    color: var(--fg-muted);
    background: var(--bg);
    transition: var(--tr);
  }
  .seg + .seg { border-left: 1px solid var(--border); }
  .seg.on {
    background: var(--accent-soft);
    color: var(--accent);
  }

  .danger {
    display: block;
    width: 100%;
    padding: 12px;
    margin-top: 6px;
    border: 1px solid var(--border);
    background: var(--bg);
    border-radius: var(--radius-lg);
    font-size: 13px;
    font-weight: 500;
    color: var(--bad);
  }
  .danger.ghost { color: var(--fg-muted); }

  .foot {
    margin: 14px 0 0;
    text-align: center;
    font-family: var(--font-mono);
    font-size: 9.5px;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: var(--fg-subtle);
  }
</style>
